'use client';

import { Button, Drawer, Heading, Icon, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';

import { useCurbsideQuery } from '@libs/common-next';
import { parseRichText } from '@libs/contentful';

import {
    StackedDrawerBackButton,
    StackedDrawerBoundary,
    type UseStackedDrawerProps,
    useStackedDrawer,
} from '../stacked-drawer-boundary';
import './CurbsideInfoDetail.scss';

type CurbsideInfoDetailProps = UseStackedDrawerProps & {
    showBackButton?: boolean;
};

export const CurbsideInfoDetail = ({
    showBackButton = false,
    forceClosed = false,
    onOpenChange,
    onCloseAll,
}: CurbsideInfoDetailProps) => {
    const { curbsideData } = useCurbsideQuery();
    const { isActive, hasBeenRendered, headingId, handleOpen, handleBack, handleClose } = useStackedDrawer({
        forceClosed,
        onOpenChange,
        onCloseAll,
    });

    if (!curbsideData) return null;

    const { header, footer, body } = curbsideData;
    const parsedFooter = footer ? parseRichText(footer) : null;
    const parsedBody = body ? parseRichText(body) : null;

    return (
        <div className="curbside-info-detail" data-testid="curbside-info">
            <Icon name="car" className="curbside-info-detail__icon" />
            <TextPassage>Curbside pickup available</TextPassage>
            <Button
                type="button"
                variant="link"
                className="curbside-info-detail__btn"
                text="Details"
                onClick={handleOpen}
            />
            {hasBeenRendered && (
                <StackedDrawerBoundary>
                    <Drawer
                        align="right"
                        isActive={isActive}
                        onClose={handleClose}
                        data-testid="curbside-info-drawer"
                        className={cn('curbside-info-drawer', { 'curbside-info-drawer--stacked': showBackButton })}
                        ariaLabelledBy={headingId}
                    >
                        <Drawer.Header className="curbside-info-drawer__header">
                            {showBackButton && (
                                <StackedDrawerBackButton
                                    className="curbside-info-drawer__back"
                                    testId="curbside-info-drawer-back-btn"
                                    onClick={handleBack}
                                />
                            )}
                            <Heading id={headingId} tagName="h2" size="headline" data-testid="drawer-title">
                                {header}
                            </Heading>
                        </Drawer.Header>
                        <Drawer.Body className="curbside-info-drawer__content" data-testid="drawer-body">
                            {parsedBody}
                        </Drawer.Body>
                        {parsedFooter && <Drawer.Footer data-testid="drawer-footer">{parsedFooter}</Drawer.Footer>}
                    </Drawer>
                </StackedDrawerBoundary>
            )}
        </div>
    );
};
